import { Component, computed, DestroyRef, inject, signal, effect } from '@angular/core';
import { UserContactDetailsRequest, UserProfileUpdateRequest, UserService } from '@core/services/user';
import { Gradientsegmentbar } from "@shared/components/gradientsegmentbar/gradientsegmentbar";
import { User } from '@core/models/user';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { PhoneValidators } from '@shared/validators/phone.validator';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';

@Component({
  selector: 'app-my-account',
  imports: [Gradientsegmentbar, ReactiveFormsModule, MdbFormsModule, MdbValidationModule, MdbSelectModule],
  templateUrl: './my-account.html',
  styleUrl: './my-account.scss',
})
export class MyAccount {
  private destroyRef = inject(DestroyRef);
  private userService = inject(UserService);

  isLoading = signal<boolean>(false);
  screenWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);
  isMobile = computed(() => this.screenWidth() < 768);
  protected readonly user = signal<User | null>(null);
  private originalValues = signal<{ email: string; phone: string }>({ email: '', phone: '' });
  alertState = signal<{ type: 'error' | 'success' | 'info' | 'warning'; message: string; } | null>(null);

  constructor() {
    const userInfo = this.userService.getUserInfoFromStorage();
    const getUserRequest: UserContactDetailsRequest = {
      email: userInfo?.email ?? '',
      phoneNumber: userInfo?.phoneNumber ?? '',
    };
    this.isLoading.set(true);

    this.userService.getUserInfo(getUserRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => this.user.set(user));

    effect(() => {
      const u = this.user();
      if (!u) return;

      const email = u.email ?? '';
      const phone = u.phoneNumber ?? '';
      this.originalValues.set({ email, phone });

      this.accountForm.patchValue({
        address1: u.address1 ?? '',
        address2: u.address2 ?? '',
        address3: u.address3 ?? '',
        city: u.city ?? '',
        state: u.state || 'CA',
        zipCode: u.zipCode ?? '',
        preferredContact: this.singleContactMethod() ?? (u.preferredContact ?? ''),
        preferredLanguage: u.preferredLanguage ?? '',
      });

      this.isLoading.set(false);
    });


  }



  //compute the default contact for a users preferred contact
  protected readonly singleContactMethod = computed(() => {
    const u = this.user();
    if (!u) return null;

    const hasEmail = !!u.email?.trim();
    const hasPhone = !!u.phoneNumber?.trim();
    // only true if both email and phone are set
    const onlyOneMethod = hasEmail !== hasPhone; 

    if (!onlyOneMethod) return null;
    return hasEmail ? 'email' : 'phone';
  });

  //#region Account Information Form
  accountForm = new FormGroup({
    /*firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.email, this.contactPairValidator('email')]),
    confirmEmail: new FormControl(''),
    phone: new FormControl('', [PhoneValidators.usaPhone, this.contactPairValidator('phone')]),
    confirmPhone: new FormControl(''),*/
    address1: new FormControl(''),
    address2: new FormControl(''),
    address3: new FormControl(''),
    city: new FormControl(''),
    state: new FormControl('CA'),
    zipCode: new FormControl('', [Validators.pattern(/^\d{5}$/)]),
    preferredContact: new FormControl(''),
    preferredLanguage: new FormControl(''),
  });


  // Computed flags for showing confirm fields
  private readonly formValues = toSignal(this.accountForm.valueChanges);



  protected readonly emailChanged = computed(() => {
    this.formValues(); // subscribe to changes
    return this.accountForm.get('email')?.value !== this.originalValues().email;
  });

  protected readonly phoneChanged = computed(() => {
    this.formValues(); // subscribe to changes
    return this.accountForm.get('phone')?.value !== this.originalValues().phone;
  });
  //only show options for available contacts. If a user only has 1 of the contacts only show that method in the dropdown.
  protected readonly availableContactMethods = computed(() => {
    const u = this.user();
    if (!u) return [];

    const hasEmail = !!u.email?.trim();
    const hasPhone = !!u.phoneNumber?.trim();

    return this.contactMethods.filter(method =>
      (method.value === 'email' && hasEmail) ||
      (method.value === 'phone' && hasPhone)
    );
  });

  // Account form getters
  get firstName(): AbstractControl { return this.accountForm.get('firstName')!; }
  get lastName(): AbstractControl { return this.accountForm.get('lastName')!; }
  get emailField(): AbstractControl { return this.accountForm.get('email')!; }
  get confirmEmailField(): AbstractControl { return this.accountForm.get('confirmEmail')!; }
  get phoneField(): AbstractControl { return this.accountForm.get('phone')!; }
  get confirmPhoneField(): AbstractControl { return this.accountForm.get('confirmPhone')!; }
  get zipCodeField(): AbstractControl { return this.accountForm.get('zipCode')!; }


  // At least one contact method required
  private atLeastOneContactValidator(group: AbstractControl): ValidationErrors | null {
    const email = group.get('email')?.value;
    const phone = group.get('phone')?.value;
    return email || phone ? null : { atLeastOneContact: true };
  }

  // If email or phone changed, confirm field must be present and match
  private confirmContactValidator(group: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    const email = group.get('email')?.value ?? '';
    const confirmEmail = group.get('confirmEmail')?.value ?? '';
    const phone = group.get('phone')?.value ?? '';
    const confirmPhone = group.get('confirmPhone')?.value ?? '';

    if (email !== this.originalValues().email) {
      if (!confirmEmail) {
        errors['confirmEmailRequired'] = true;
      } else if (email !== confirmEmail) {
        errors['confirmEmailMismatch'] = true;
      }
    }

    if (phone !== this.originalValues().phone) {
      if (!confirmPhone) {
        errors['confirmPhoneRequired'] = true;
      } else if (phone !== confirmPhone) {
        errors['confirmPhoneMismatch'] = true;
      }
    }

    return Object.keys(errors).length ? errors : null;
  }

  // Either field can be empty as long as the other has a value
  private contactPairValidator(currentField: 'email' | 'phone'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value?.trim()) return null;

      const pairedFieldName = currentField === 'email' ? 'phone' : 'email';
      const otherControl = control.parent?.get(pairedFieldName);

      if (otherControl?.value?.trim()) return null;

      if ((control.dirty || control.touched) && (otherControl?.dirty || otherControl?.touched)) {
        return { contactRequired: true };
      }

      return null;
    };
  }

  // Update account information
  async updateAccount(event: Event): Promise<void> {
    event.preventDefault();

    if (this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      return;
    }

    //if only 1 contact method exists for the user always default to that method
    const single = this.singleContactMethod();
    if (single && !this.accountForm.get('preferredContact')?.value) {
      this.accountForm.get('preferredContact')?.setValue(single);
    }

    const currentUser = this.user();
    if (!currentUser) return;

    this.isLoading.set(true);
    this.alertState.set(null);

    const formValue = this.accountForm.value;

    const request: UserProfileUpdateRequest = {

      userId: currentUser.userId,
      /*firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      userName: currentUser.userName ?? '',
      email: currentUser.email,
      preferredName: currentUser.preferredName ?? '',
      */
      address1: formValue.address1 ?? '',
      address2: formValue.address2 ?? '',
      address3: formValue.address3 ?? '',
      city: formValue.city ?? '',
      state: formValue.state ?? '',
      zipCode: formValue.zipCode ?? '',
      preferredContact: formValue.preferredContact ?? '',
      preferredLanguage: formValue.preferredLanguage ?? '',
      modifiedBy: currentUser.userId,
    };

    
    this.userService.updateUserProfile(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedUser) => {
          this.user.set(updatedUser);
          this.originalValues.set({
            email: updatedUser.email ?? '',
            phone: updatedUser.phoneNumber ?? '',
          });
          this.accountForm.markAsPristine();
          this.isLoading.set(false);
          this.alertState.set({ type: 'success', message: 'Account information updated successfully.' });
        },
        error: () => {
          this.isLoading.set(false);
          this.alertState.set({ type: 'error', message: 'Something went wrong updating your account. Please try again.' });
        },
      });
  }
  //#endregion

  protected readonly states: { value: string; label: string }[] = [
    { value: 'AL', label: 'AL - Alabama' },
    { value: 'AK', label: 'AK - Alaska' },
    { value: 'AZ', label: 'AZ - Arizona' },
    { value: 'AR', label: 'AR - Arkansas' },
    { value: 'CA', label: 'CA - California' },
    { value: 'CO', label: 'CO - Colorado' },
    { value: 'CT', label: 'CT - Connecticut' },
    { value: 'DE', label: 'DE - Delaware' },
    { value: 'FL', label: 'FL - Florida' },
    { value: 'GA', label: 'GA - Georgia' },
    { value: 'HI', label: 'HI - Hawaii' },
    { value: 'ID', label: 'ID - Idaho' },
    { value: 'IL', label: 'IL - Illinois' },
    { value: 'IN', label: 'IN - Indiana' },
    { value: 'IA', label: 'IA - Iowa' },
    { value: 'KS', label: 'KS - Kansas' },
    { value: 'KY', label: 'KY - Kentucky' },
    { value: 'LA', label: 'LA - Louisiana' },
    { value: 'ME', label: 'ME - Maine' },
    { value: 'MD', label: 'MD - Maryland' },
    { value: 'MA', label: 'MA - Massachusetts' },
    { value: 'MI', label: 'MI - Michigan' },
    { value: 'MN', label: 'MN - Minnesota' },
    { value: 'MS', label: 'MS - Mississippi' },
    { value: 'MO', label: 'MO - Missouri' },
    { value: 'MT', label: 'MT - Montana' },
    { value: 'NE', label: 'NE - Nebraska' },
    { value: 'NV', label: 'NV - Nevada' },
    { value: 'NH', label: 'NH - New Hampshire' },
    { value: 'NJ', label: 'NJ - New Jersey' },
    { value: 'NM', label: 'NM - New Mexico' },
    { value: 'NY', label: 'NY - New York' },
    { value: 'NC', label: 'NC - North Carolina' },
    { value: 'ND', label: 'ND - North Dakota' },
    { value: 'OH', label: 'OH - Ohio' },
    { value: 'OK', label: 'OK - Oklahoma' },
    { value: 'OR', label: 'OR - Oregon' },
    { value: 'PA', label: 'PA - Pennsylvania' },
    { value: 'RI', label: 'RI - Rhode Island' },
    { value: 'SC', label: 'SC - South Carolina' },
    { value: 'SD', label: 'SD - South Dakota' },
    { value: 'TN', label: 'TN - Tennessee' },
    { value: 'TX', label: 'TX - Texas' },
    { value: 'UT', label: 'UT - Utah' },
    { value: 'VT', label: 'VT - Vermont' },
    { value: 'VA', label: 'VA - Virginia' },
    { value: 'WA', label: 'WA - Washington' },
    { value: 'WV', label: 'WV - West Virginia' },
    { value: 'WI', label: 'WI - Wisconsin' },
    { value: 'WY', label: 'WY - Wyoming' },
    { value: 'DC', label: 'DC - District of Columbia' },
  ];

  protected readonly contactMethods: { value: string; label: string }[] = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
  ];

  protected readonly languages: { value: string; label: string }[] = [
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'Other', label: 'Other' },
  ];
}

